import {Component, Signal, computed} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {toSignal, toObservable} from '@angular/core/rxjs-interop';
import {Observable, catchError, combineLatest, distinctUntilChanged, filter, map, merge, of, shareReplay, switchMap, timer} from 'rxjs';
import {VtkJobOrgService} from './vtk-job-org.service';
import {VtkResult, VtkService, vtkResult} from '../../../services/vtk.service';
import {VtkJobDetail, VtkJobMetrics, VTK_STATUS_LABELS, VTK_STATUS_COLORS} from '../../../services/vtk.types';
import {ContextService} from '../../../services/context.service';
import {Organization} from '../../../types-repository';
import {Airline} from '../../../types-workflow';
import {LogoAreaComponent, BreadCrumbs} from '../../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import {DateComponent} from '../../../date/date.component';
import {DurationPipe} from '../../../pipes/duration.pipe';
import {FileSizePipe} from '../../../pipes/file-size-pipe';

@Component({
  selector: 'app-vtk-job-detail',
  template: `
    <app-logo-area [breadcrumbs]="breadCrumbs()"></app-logo-area>
    <main>
    <app-headline-section [title]="'VTK Job ' + jobId()"></app-headline-section>
    <section class="cl-section cl-section-regular">
    <div class="container"><div class="row"><div class="col-12">

    @if (result(); as result) {
      @if (result.error) {
        <p class="alert cl-alert-danger">Could not load VTK job {{ jobId() }}: {{ result.error }}</p>
      }
      @if (result.value; as job) {
        <div class="card">
          <div class="card-body">
            <dl class="cl-dl">
              <dt>Status</dt>
              <dd><span class="cl-badge cl-badge-sm" [class]="statusColor(job.status)">{{ statusLabel(job.status) }}</span></dd>
              <dt>User</dt><dd>{{ job.user || '-' }}</dd>
              <dt>Region</dt><dd>{{ job.region || '-' }}</dd>
              <dt>Priority</dt><dd>{{ job.priority }}</dd>
              <dt>Bytes in</dt><dd>{{ job.bytes_in | fileSize }}</dd>
              <dt>Bytes out</dt><dd>{{ job.bytes_out | fileSize }}</dd>
              <dt>Execution time</dt><dd>{{ job.execution_time | duration }}</dd>
              @if (metrics(); as m) {
                <dt>Compute Units</dt><dd>{{ m.cus.toFixed(2) }}</dd>
                @if (m.video_minutes_in) { <dt>Video minutes in</dt><dd>{{ m.video_minutes_in }}</dd> }
                @if (m.video_minutes_out) { <dt>Video minutes out</dt><dd>{{ m.video_minutes_out }}</dd> }
                @if (m.gb_in) { <dt>GB in</dt><dd>{{ m.gb_in.toFixed(3) }}</dd> }
                @if (m.gb_out) { <dt>GB out</dt><dd>{{ m.gb_out.toFixed(3) }}</dd> }
              }
              <dt>Created</dt><dd><app-date [value]="job.created" /></dd>
              <dt>Updated</dt><dd><app-date [value]="job.modified" /></dd>
            </dl>
          </div>
        </div>

        @if (job.tags?.length) {
          <p class="cl-spacing-box-micro">
            @for (tag of job.tags; track tag) {
              <span class="cl-badge cl-badge-sm cl-color-clay">{{ tag }}</span>
            }
          </p>
        }

        <h3 class="cl-spacing-medium">Tasks ({{ job.tasks?.length ?? 0 }})</h3>
        <table class="cl-table-data">
          <thead>
            <tr>
              <th class="cl-table-head">Tool</th>
              <th class="cl-table-head">Version</th>
              <th class="cl-table-head">Status</th>
            </tr>
          </thead>
          <tbody>
            @for (task of job.tasks; track task.id) {
              <tr class="cl-cursor-pointer" [routerLink]="['tasks', task.id]">
                <td>{{ task.tool }}</td>
                <td>{{ task.version }}</td>
                <td><span class="cl-badge cl-badge-sm" [class]="statusColor(task.status)">{{ statusLabel(task.status) }}</span></td>
              </tr>
            } @empty {
              <tr class="cl-tr-no-data"><td colspan="100">No data available.</td></tr>
            }
          </tbody>
        </table>
      }
    } @else {
      <p>Loading ...</p>
    }

    </div></div></div>
    </section>
    </main>
  `,
  styles: [`
    :host {
      height: calc(100% - 45px);
      display: table;
    }
  `],
  imports: [RouterLink, LogoAreaComponent, HeadlineSectionComponent, DateComponent, DurationPipe, FileSizePipe]
})
export class VtkJobDetailComponent {
  organization: Signal<Organization>;
  airline: Signal<Airline>;
  poName: Signal<string>;
  poItemId: Signal<string>;
  jobId: Signal<string>;
  // undefined until the first response: the page then says "Loading ..." instead of rendering nothing.
  result: Signal<VtkResult<VtkJobDetail> | undefined>;
  metrics: Signal<VtkJobMetrics | undefined>;
  breadCrumbs: Signal<BreadCrumbs>;

  constructor(
    private route: ActivatedRoute,
    private vtkService: VtkService,
    private contextService: ContextService,
    private jobOrg: VtkJobOrgService,
  ) {
    this.organization = this.contextService.organization;
    this.airline = this.contextService.airline;
    this.poName = toSignal(this.route.params.pipe(map(p => p['po']), distinctUntilChanged()), {initialValue: ''});
    this.poItemId = toSignal(this.route.params.pipe(map(p => p['poitem']), distinctUntilChanged()), {initialValue: ''});
    this.jobId = toSignal(this.route.params.pipe(map(p => p['job_id']), distinctUntilChanged()), {initialValue: ''});

    // The VTK organization comes from the PO item's record of the job (VtkJobOrgService); an
    // org_urn query parameter on an old link is ignored. Each job is paired with its own lookup only:
    // a new job ends the previous one's polling, and a failed lookup leaves nothing polling.
    const job$ = this.jobOrg.jobOrgs(this.route).pipe(shareReplay({bufferSize: 1, refCount: true}));

    // A failed poll shows why (VTK-3464: a 401 used to leave just the headline); the next
    // successful poll replaces the message.
    this.result = toSignal(job$.pipe(
      switchMap(({org, organizationId, poName, jobId}): Observable<VtkResult<VtkJobDetail> | undefined> => {
        if (!org) return of(undefined);
        // Without the organization no request can be made: say so instead of "Loading ..." forever.
        if (org.error) return of({error: org.error});
        return timer(0, 10000).pipe(switchMap(() => vtkResult(this.vtkService.getJob(org.value!, organizationId, poName, jobId))));
      }),
    ));

    this.metrics = toSignal(job$.pipe(
      switchMap(({org, organizationId, poName, jobId}) => org?.value
        ? this.vtkService.getJobMetrics(org.value, organizationId, poName, jobId).pipe(catchError(() => of(undefined)))
        : of(undefined)),
    ));

    this.breadCrumbs = computed(() => {
      const org_id = this.organization()?.id;
      const airline = this.airline();
      const po = this.poName();
      const poItemId = this.poItemId();
      if (!org_id || !airline) return [];
      return [
        {display: 'Content Platform', commands: ['/o', org_id]},
        {display: 'Workflows', commands: ['/o', org_id, 'workflows']},
        {display: airline.airline_name, commands: ['/o', org_id, 'workflows', airline.iata_code]},
        {display: po, commands: ['/o', org_id, 'workflows', airline.iata_code, po]},
        {display: poItemId, commands: ['/o', org_id, 'workflows', airline.iata_code, po, poItemId]},
      ];
    });
  }

  statusLabel(status: number): string { return VTK_STATUS_LABELS[status] ?? 'Unknown'; }
  statusColor(status: number): string { return VTK_STATUS_COLORS[status] ?? 'cl-color-ash'; }
}
